import { createFileRoute } from '@tanstack/react-router'
import { Container } from '../../components/ui/Container'
import { Button } from '../../components/ui/Button'
import { FileText, GraduationCap, Phone, ArrowRight, Award, Clock, Target, Check, X } from 'lucide-react'
import { formatPrice } from '../../lib/utils/format'
import { generateWhatsAppLink } from '../../lib/utils/whatsapp'

// Data Structures
const packages = [
  {
    id: 'basic',
    name: 'Paket Basic',
    priceA5: 2000000,
    description: 'Layanan konversi KTI menjadi buku dengan fasilitas standar untuk kebutuhan akademik',
    features: [
      { text: 'Ubah Judul', included: true },
      { text: 'Ubah Struktur Bab', included: true },
      { text: 'Parafrase Advance', included: true },
      { text: 'Cover dan Layout', included: true },
      { text: 'Proofreading', included: true },
      { text: 'ISBN', included: true },
      { text: 'Terbit 10 Eks', included: true },
      { text: 'Profit Share 15% Buku Cetak & 25% Buku Digital', included: true },
      { text: 'Profit Share 5% Proyek Pengadaan Buku', included: true },
      { text: 'Indeks Google Scholar', included: true },
      { text: 'Sertifikat Penulis', included: true },
      { text: 'Gratis Ebook', included: true },
      { text: 'Promosi Media Sosial', included: true },
      { text: 'Mockup Promosi', included: true },
      { text: 'Free HKI', included: true },
      { text: 'Cek Plagiasi', included: false },
      { text: 'Konten Promosi', included: false },
      { text: 'Turunkan Plagiasi Hingga 25%', included: false },
    ],
    popular: false,
  },
  {
    id: 'premium',
    name: 'Paket Premium',
    priceA5: 3500000,
    description: 'Solusi lengkap konversi KTI dengan fasilitas maksimal, eks lebih banyak, dan layanan plagiasi',
    features: [
      { text: 'Ubah Judul', included: true },
      { text: 'Ubah Struktur Bab', included: true },
      { text: 'Parafrase Advance', included: true },
      { text: 'Cover dan Layout', included: true },
      { text: 'Proofreading', included: true },
      { text: 'ISBN', included: true },
      { text: 'Terbit 20 Eks', included: true },
      { text: 'Profit Share 15% Buku Cetak & 25% Buku Digital', included: true },
      { text: 'Profit Share 5% Proyek Pengadaan Buku', included: true },
      { text: 'Indeks Google Scholar', included: true },
      { text: 'Sertifikat Penulis', included: true },
      { text: 'Gratis Ebook', included: true },
      { text: 'Promosi Media Sosial', included: true },
      { text: 'Mockup Promosi', included: true },
      { text: 'Free HKI', included: true },
      { text: 'Cek Plagiasi', included: true },
      { text: 'Konten Promosi', included: true },
      { text: 'Turunkan Plagiasi Hingga 25%', included: true },
    ],
    popular: true,
  },
]

const ktiTypes = [
  {
    icon: FileText,
    title: 'Skripsi',
    description: 'Konversi skripsi S1 menjadi buku monograf dengan standar penerbitan akademik',
    duration: '2-3 minggu',
  },
  {
    icon: GraduationCap,
    title: 'Tesis',
    description: 'Publikasi tesis S2 dalam format buku yang lebih accessible untuk komunitas akademik',
    duration: '3-4 minggu',
  },
  {
    icon: Award,
    title: 'Disertasi',
    description: 'Konversi disertasi S3 menjadi buku komprehensif untuk kontribusi ilmiah',
    duration: '4-6 minggu',
  },
  {
    icon: Target,
    title: 'Laporan Penelitian',
    description: 'Transformasi laporan penelitian menjadi publikasi yang lebih luas jangkauannya',
    duration: '2-3 minggu',
  },
]

const steps = [
  {
    number: '01',
    title: 'Konsultasi & Review',
    description: 'Tim kami akan mereview KTI Anda dan memberikan rekomendasi paket yang sesuai dengan kebutuhan.',
  },
  {
    number: '02',
    title: 'Editing & Layout',
    description: 'Proses editing konten, proofreading, dan layouting sesuai standar penerbitan profesional.',
  },
  {
    number: '03',
    title: 'Desain & Produksi',
    description: 'Pembuatan desain cover berkualitas dan proses cetak menggunakan teknologi modern.',
  },
  {
    number: '04',
    title: 'Publikasi & Distribusi',
    description: 'Pengurusan ISBN dan distribusi buku ke berbagai platform penjualan (khusus paket premium).',
  },
]

const benefits = [
  {
    icon: Award,
    title: 'Karya Abadi',
    description: 'Ubah KTI yang terbatas jangkauannya menjadi buku yang dapat dinikmati banyak pembaca dan menjadi referensi akademik.',
  },
  {
    icon: Clock,
    title: 'Proses Cepat',
    description: 'Dengan pengalaman menangani ratusan konversi KTI, kami menyelesaikan proses dalam waktu 2-4 minggu tergantung kompleksitas.',
  },
  {
    icon: Target,
    title: 'Legal & Terpercaya',
    description: 'Seluruh proses sesuai aturan penerbitan yang berlaku, termasuk pengurusan ISBN resmi dari Perpustakaan Nasional.',
  },
]

export const Route = createFileRoute('/konversi-kti/')({
  component: KonversiKTI,
})

// Function dengan parameter yang fleksibel
const handlePackageOrder = ({
  pkgName,
  price,
  size
}: {
  pkgName: string
  price?: number
  size?: 'A5' | 'Unesco'
}) => {
  let message: string
  if (price && size) {
    message = `Halo, saya tertarik dengan ${pkgName} (Ukuran ${size}) - ${formatPrice(price)}`
  } else {
    message = `Halo, saya tertarik dengan ${pkgName}. Mohon info lebih lanjut.`
  }
  const link = generateWhatsAppLink(message)
  window.open(link, '_blank')
}

function KonversiKTI() {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-slate-800 text-white">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-yellow-400 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-slate-700 rounded-full blur-3xl" />
        </div>

        <Container className="relative z-10 py-16 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-brand-yellow-300 text-xs font-medium tracking-wide mb-6">
              <FileText className="w-4 h-4" />
              LAYANAN UNGGULAN
            </div>

            <h1 className="text-3xl md:text-4xl text-white tracking-tight mb-4">
              Konversi Karya Tulis Ilmiah
              <span className="text-brand-yellow-400 block mt-1"> Menjadi Buku Berkualitas</span>
            </h1>

            <p className="text-brand-slate-400 text-base max-w-2xl mx-auto">
              Transformasikan skripsi, tesis, atau disertasi Anda menjadi buku yang <br /> dipublikasikan secara profesional.
            </p>
          </div>
        </Container>
      </section>

      {/* Pricing Packages Section */}
      <section className="py-16 lg:py-20 bg-brand-slate-50">
        <Container>
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-yellow-100 text-brand-yellow-700 text-xs font-medium tracking-wide mb-4 border border-brand-yellow-200">
              HARGA & PAKET
            </div>
            <h2 className="text-2xl md:text-3xl text-brand-slate-800 tracking-tight mb-4">
              Pilih Paket yang Sesuai
            </h2>
            <p className="text-brand-slate-500 max-w-2xl mx-auto">
              Dua pilihan paket yang dirancang untuk memenuhi kebutuhan berbagai jenjang akademik dan anggaran
            </p>
          </div>

          {/* Packages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative bg-white border rounded-xl p-6 transition-all duration-300 hover:shadow-lg flex flex-col ${
                  pkg.popular
                    ? 'border-brand-yellow-400 ring-1 ring-brand-yellow-400'
                    : 'border-brand-slate-200'
                }`}
              >
                {/* Popular Badge */}
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-yellow-400 text-brand-slate-900 text-xs font-semibold px-3 py-1 rounded-full">
                      PALING POPULER
                    </span>
                  </div>
                )}

                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-brand-slate-800">{pkg.name}</h3>
                  <p className="text-sm text-brand-slate-500 mt-1">{pkg.description}</p>
                </div>

                {/* Price - Hanya A5 */}
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-brand-slate-900">{formatPrice(pkg.priceA5)}</div>
                  <div className="text-sm text-brand-slate-400 mt-1">Ukuran A5</div>
                </div>

                {/* Features */}
                <div className="space-y-2 mb-6 grow">
                  {pkg.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-brand-yellow-500 mt-0.5 shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-brand-slate-300 mt-0.5 shrink-0" />
                      )}
                      <span className={feature.included ? 'text-brand-slate-700' : 'text-brand-slate-400 line-through'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button - Single */}
                <Button
                  onClick={() => handlePackageOrder({
                    pkgName: pkg.name,
                    price: pkg.priceA5,
                    size: 'A5'
                  })}
                  className={`w-full h-11 cursor-pointer rounded-lg font-semibold text-sm transition-all ${
                    pkg.popular
                      ? 'bg-brand-yellow-400 hover:bg-brand-yellow-500 text-brand-slate-900 shadow-sm hover:shadow'
                      : 'bg-brand-slate-800 hover:bg-brand-slate-900 text-white'
                  }`}
                >
                  Pilih Paket
                </Button>
              </div>
            ))}
          </div>

          {/* Optional: Note untuk Unesco */}
          <p className="text-center text-sm text-brand-slate-400 mt-8">
            *Tersedia ukuran Unesco dengan harga berbeda. Hubungi kami untuk informasi lebih lanjut.
          </p>
        </Container>
      </section>

      {/* Supported KTI Types Section - Editorial Minimalist */}
      {/* Supported KTI Types Section - Editorial Minimalist */}
      <section className="py-20 lg:py-28 bg-white">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              {/* Left Column - Header */}
              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-32">
                  <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-600 uppercase mb-4 block">
                    Jenis KTI
                  </span>
                  <h2 className="text-3xl lg:text-4xl font-light text-brand-slate-900 leading-tight mb-6">
                    Karya Tulis yang <br />
                    <span className="font-medium">Kami Dukung</span>
                  </h2>
                  <p className="text-brand-slate-500 leading-relaxed">
                    Berbagai jenis karya tulis ilmiah dapat dikonversi menjadi buku dengan standar penerbitan profesional
                  </p>

                  {/* Decorative Line */}
                  <div className="w-16 h-0.5 bg-brand-yellow-400 mt-8" />
                </div>
              </div>

              {/* Right Column - Cards */}
              <div className="lg:col-span-8 space-y-4">
                {ktiTypes.map((type, index) => {
                  const Icon = type.icon
                  return (
                    <div
                      key={type.title}
                      className="group flex items-start gap-6 p-6 rounded-xl border border-brand-slate-100 hover:border-brand-yellow-200 hover:bg-brand-slate-50/50 transition-all duration-300"
                    >
                      {/* Index Number */}
                      <span className="text-3xl font-light text-brand-slate-200 group-hover:text-brand-yellow-400 transition-colors w-12 shrink-0">
                        {String(index + 1).padStart(2, '0')}
                      </span>

                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-medium text-brand-slate-900">
                            {type.title}
                          </h3>
                          <div className="flex items-center gap-1.5 text-xs text-brand-slate-400">
                            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {type.duration}
                          </div>
                        </div>
                        <p className="text-brand-slate-500 leading-relaxed text-sm">
                          {type.description}
                        </p>
                      </div>

                      {/* Icon */}
                      <div className="w-10 h-10 rounded-full bg-brand-slate-100 group-hover:bg-brand-yellow-400 flex items-center justify-center transition-colors shrink-0">
                        <Icon className="w-5 h-5 text-brand-slate-600 group-hover:text-brand-slate-900" strokeWidth={1.5} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Process Section - Editorial Timeline */}
      <section className="py-20 lg:py-28 bg-brand-slate-50">
        <Container>
          <div className="max-w-6xl mx-auto">
            {/* Header Center */}
            <div className="max-w-2xl mx-auto text-center mb-16">
              <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-600 uppercase mb-4 block">
                Proses Kerja
              </span>
              <h2 className="text-3xl lg:text-4xl font-light text-brand-slate-900 mb-6">
                Bagaimana <span className="font-medium">Proses Konversi?</span>
              </h2>
              <p className="text-brand-slate-500 leading-relaxed">
                Proses konversi KTI menjadi buku yang sistematis dan transparan
              </p>
            </div>

            {/* Process Steps - Minimalist Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <div key={step.number} className="relative group">
                  {/* Connector */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-brand-slate-200 z-0" />
                  )}

                  <div className="relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 h-full border border-transparent hover:border-brand-yellow-200 flex flex-col">
                    {/* Header dengan nomor */}
                    <div className="flex justify-between items-start mb-4 min-h-12">
                      <div className="w-8 h-0.5 bg-brand-yellow-400 mt-4" />
                      <span className="text-5xl font-light text-brand-slate-100 group-hover:text-brand-yellow-100 transition-colors tabular-nums leading-none">
                        {step.number}
                      </span>
                    </div>

                    {/* Content */}
                    <h3 className="text-lg font-medium text-brand-slate-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm text-brand-slate-500 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Benefits Section - Editorial Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <Container>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left - Large Typography */}
              <div>
                <span className="text-xs font-medium tracking-[0.2em] text-brand-yellow-600 uppercase mb-6 block">
                  Keuntungan
                </span>
                <h2 className="text-4xl lg:text-5xl font-light text-brand-slate-900 leading-[1.1] mb-8">
                  Mengapa Konversi
                  <span className="font-medium block mt-2">KTI Anda?</span>
                </h2>
                <p className="text-brand-slate-500 text-lg leading-relaxed mb-8 max-w-md">
                  Konversi KTI memberikan nilai tambah yang signifikan bagi perjalanan akademik dan profesional Anda
                </p>

                {/* CTA Button */}
                <Button
                  onClick={() => handlePackageOrder({ pkgName: 'Konsultasi Gratis' })}
                  className="bg-brand-slate-900 hover:bg-brand-slate-800 text-white px-8 py-3 rounded-full font-medium transition-all cursor-pointer"
                >
                  Mulai Konsultasi
                </Button>
              </div>

              {/* Right - Benefits List */}
              <div className="space-y-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon
                  return (
                    <div key={benefit.title} className="flex gap-6 group">
                      {/* Large Index */}
                      <div className="shrink-0">
                        <span className="text-4xl font-light text-brand-slate-200 group-hover:text-brand-yellow-400 transition-colors">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>

                      <div className="pb-8 border-b border-brand-slate-100 last:border-0 last:pb-0 flex-1">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-brand-slate-50 group-hover:bg-brand-yellow-400 flex items-center justify-center transition-colors shrink-0">
                            <Icon className="w-5 h-5 text-brand-slate-600 group-hover:text-brand-slate-900" strokeWidth={1.5} />
                          </div>
                          <div>
                            <h3 className="text-xl font-medium text-brand-slate-900 mb-2">
                              {benefit.title}
                            </h3>
                            <p className="text-brand-slate-500 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-brand-slate-50">
        <Container>
          <div className="relative overflow-hidden rounded-2xl bg-brand-slate-800 p-8 lg:p-12">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-brand-yellow-400 rounded-full blur-3xl" />
              <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-brand-slate-400 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 text-center">
              <h3 className="text-2xl lg:text-3xl text-white mb-4">
                Siap Mengonversi KTI Anda?
              </h3>
              <p className="text-brand-slate-300 mb-8 max-w-xl mx-auto">
                Konsultasikan kebutuhan Anda dengan tim ahli kami dan dapatkan penawaran terbaik untuk publikasi karya ilmiah Anda.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => handlePackageOrder({ pkgName: 'Konsultasi Gratis' })}
                  className="px-6 py-2.5 cursor-pointer bg-brand-yellow-400 hover:bg-brand-yellow-500 text-brand-slate-900 font-medium rounded-lg transition-all shadow-lg shadow-brand-yellow-400/20 text-sm border-0"
                >
                  <Phone className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Konsultasi Gratis
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className="px-6 py-2.5 cursor-pointer bg-brand-slate-700 hover:bg-brand-yellow-500 border border-white/20 text-white hover:text-brand-slate-900 font-medium rounded-lg backdrop-blur-sm transition-all text-sm"
                >
                  <a href="/penerbitan" className="flex items-center justify-center">
                    Lihat Layanan Penerbitan
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  )
}
